import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNotificationsAskAgain } from '@d-orca/shared/client/hooks';
import { receiveAlertsFrame } from '@d-orca/shared/client/notifications';
import { NotificationsStream } from '@apis/notificationsStream';

/**
 * Keeps what the bell shows current, on any page: holds the signed-in
 * person's notifications stream — the site's, once there is one — and hands
 * its signals to the shared "ask again", which refetches the list for the
 * site on screen (`notificationsAskAgain` has the rules), and its alert
 * frames to the site's alert list. A site switch opens the new site's stream.
 */
export function useNotificationsStream(siteId: string | null): void {
  const askAgain = useNotificationsAskAgain(siteId);
  const queryClient = useQueryClient();

  useEffect(() => {
    const stream = new NotificationsStream(siteId, {
      onEvent: (event) => {
        if (event.channel === 'alerts.reset' || event.channel === 'alerts.upsert') receiveAlertsFrame(queryClient, event);
        else askAgain.onSignal(event);
      },
      // The stream reconnects itself; what it may have missed while it was
      // down — or before it first opened — is covered by asking again.
      onOpen: askAgain.onOpen,
      onTokenExpiring: () => {},
      onClose: () => {},
      onError: () => {},
    });
    stream.open();
    return () => stream.close();
  }, [askAgain, queryClient, siteId]);
}
